import { supabase } from '@/integrations/supabase/client';

export interface User {
  id: string;
  name: string;
  email: string;
  type: 'Admin' | 'Mentor' | 'Mentee' | 'User';
  status: 'Active' | 'Inactive' | 'Pending' | 'Locked';
  firstName?: string;
  lastName?: string;
  profileType?: string;
  profession?: string;
  location?: string;
  bio?: string;
  specialties?: string[];
  applicationFeePercent?: number;
  isAdmin?: boolean;
}

export interface Course {
  id: string;
  title: string;
  description: string;
  courseType: 'subscription' | 'call';
  price: number;
  duration?: number;
  author?: string;
  mentorId: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  updatedAt: string;
  instructor?: string;
  category?: string;
  rejectionReason?: string;
}

export interface PendingAccount {
  id: string | number;
  name?: string;
  email: string;
  type?: string;
  profileType?: string;
  appliedAt: string;
  details?: any;
  qualification_document_url?: string;
}

export async function loadAllUsers(): Promise<User[]> {
  try {
    const { data: profilesData, error: profilesError } = await supabase
      .from('profiles')
      .select('*');
    
    if (profilesError) {
      console.error("Error fetching profiles from Supabase:", profilesError);
      return [];
    }
    
    const users: User[] = profilesData.map(profile => ({
      id: profile.id,
      name: profile.first_name && profile.last_name ? 
        `${profile.first_name} ${profile.last_name}` : profile.email,
      email: profile.email,
      firstName: profile.first_name,
      lastName: profile.last_name,
      profileType: profile.profile_type,
      type: profile.profile_type === 'mentor' ? 'Mentor' : 
            profile.profile_type === 'mentee' ? 'Mentee' : 
            profile.profile_type === 'admin' || profile.is_admin ? 'Admin' : 'User',
      status: (profile.status as 'Active' | 'Inactive' | 'Pending' | 'Locked') || 'Pending',
      profession: profile.profession || undefined,
      location: profile.location || undefined,
      bio: profile.bio || undefined,
      specialties: profile.specialties ? JSON.parse(profile.specialties) : undefined,
      applicationFeePercent: profile.application_fee_percent || undefined,
      isAdmin: profile.is_admin || false
    }));
    
    return users;
  } catch (error) {
    console.error("Error in loadAllUsers:", error);
    return [];
  }
}

export async function loadPendingCourses(): Promise<Course[]> {
  try {
    console.log("Loading pending courses from Supabase...");
    const { data, error } = await supabase
      .from('courses')
      .select('*')
      .eq('status', 'pending');

    if (error) {
      console.error("Error loading pending courses:", error);
      return [];
    }

    console.log("Raw pending courses data from Supabase:", data);

    if (!data || data.length === 0) {
      console.log("No pending courses found in Supabase");
      return [];
    }

    // Fetch mentor profile data for each course
    const mentorIds = [...new Set(data.map(course => course.mentor_id))];
    const { data: mentorProfiles, error: mentorError } = await supabase
      .from('profiles')
      .select('id, first_name, last_name, email')
      .in('id', mentorIds);

    if (mentorError) {
      console.error("Error loading mentor profiles:", mentorError);
    }

    // Create a map of mentor profiles for quick lookup
    const mentorMap = new Map();
    if (mentorProfiles) {
      mentorProfiles.forEach(mentor => {
        mentorMap.set(mentor.id, mentor);
      });
    }

    const mappedCourses = data.map(course => {
      const mentor = mentorMap.get(course.mentor_id);
      const authorName = mentor ? `${mentor.first_name || ''} ${mentor.last_name || ''}`.trim() : 'Unknown';
      
      return {
        id: course.id, // This is the UUID primary key
        title: course.title,
        description: course.description,
        courseType: course.course_type as 'subscription' | 'call',
        price: course.price,
        duration: course.duration,
        author: authorName,
        mentorId: course.mentor_id,
        status: course.status as 'pending' | 'approved' | 'rejected',
        createdAt: course.created_at,
        updatedAt: course.updated_at,
        instructor: authorName,
        category: course.course_type === 'subscription' ? 'Subscription' : 'Voice/Video Call',
        rejectionReason: course.rejection_reason
      };
    });

    console.log("Mapped pending courses:", mappedCourses);
    return mappedCourses;
  } catch (error) {
    console.error("Error in loadPendingCourses:", error);
    return [];
  }
}

export async function loadPendingAccounts(): Promise<PendingAccount[]> {
  try {
    const { data: profilesData, error: profilesError } = await supabase
      .from('profiles')
      .select('*')
      .eq('status', 'Pending');
    
    if (profilesError) {
      console.error("Error fetching pending profiles from Supabase:", profilesError);
      return [];
    }
    
    const pendingAccounts: PendingAccount[] = profilesData.map(profile => ({
      id: profile.id,
      name: profile.first_name && profile.last_name ? 
        `${profile.first_name} ${profile.last_name}` : profile.email,
      email: profile.email,
      type: profile.profile_type === 'mentor' ? 'Mentor' : 
            profile.profile_type === 'mentee' ? 'Mentee' : 'User',
      profileType: profile.profile_type,
      appliedAt: profile.created_at || new Date().toISOString(),
      qualification_document_url: profile.qualification_document_url,
      details: {
        firstName: profile.first_name,
        lastName: profile.last_name,
        profileType: profile.profile_type
      }
    }));
    
    return pendingAccounts;
  } catch (error) {
    console.error("Error in loadPendingAccounts:", error);
    return [];
  }
}

export function addPendingCourse(course: Course): void {
  console.log("Adding pending course:", course);
}

export async function approveCourse(courseId: string): Promise<Course[]> {
  try {
    console.log("Attempting to approve course with ID:", courseId);
    
    const { data: session } = await supabase.auth.getSession();
    if (!session.session) {
      console.error("No active session found");
      throw new Error("Authentication required");
    }
    
    // First, get the course details before approval
    const { data: courseData, error: courseError } = await supabase
      .from('courses')
      .select('id, title, mentor_id, status')
      .eq('id', courseId)
      .single();
      
    if (courseError) {
      console.error("Error fetching course details:", courseError);
      throw new Error(`Course fetch failed: ${courseError.message}`);
    }
    
    if (!courseData) {
      console.error("Course not found:", courseId);
      throw new Error("Course not found");
    }
    
    console.log("Found course to approve:", courseData);
    
    // Approve the course
    const { data, error } = await supabase
      .from('courses')
      .update({ 
        status: 'approved',
        updated_at: new Date().toISOString()
      })
      .eq('id', courseId);

    if (error) {
      console.error("Error approving course:", error);
      throw error;
    }

    console.log("Course approval successful:", data);
    
    // Create notification for the mentor
    try {
      const { error: notificationError } = await supabase
        .from('notifications')
        .insert({
          receiver_id: courseData.mentor_id,
          type: 'course_approved',
          content: `Your course "${courseData.title}" has been approved and is now live.`,
          link: `/my-academy?courseId=${courseId}`,
          read: false
        });

      if (notificationError) {
        console.error("Error creating notification:", notificationError);
        // Don't throw here as the course approval was successful
      } else {
        console.log("✅ Notification created successfully for mentor:", courseData.mentor_id);
      }
    } catch (notificationErr) {
      console.error("Exception creating notification:", notificationErr);
      // Don't throw here as the course approval was successful
    }
    
    return loadPendingCourses();
  } catch (error) {
    console.error("Error in approveCourse:", error);
    throw error;
  }
}

export async function rejectCourse(courseId: string): Promise<Course[]> {
  try {
    console.log("Attempting to reject course with ID:", courseId);
    
    // First, get the course details before rejection
    const { data: courseData, error: courseError } = await supabase
      .from('courses')
      .select('id, title, mentor_id, status')
      .eq('id', courseId)
      .single();
      
    if (courseError) {
      console.error("Error fetching course details:", courseError);
      throw new Error(`Course fetch failed: ${courseError.message}`);
    }
    
    if (!courseData) {
      console.error("Course not found:", courseId);
      throw new Error("Course not found");
    }
    
    console.log("Found course to reject:", courseData);
    
    const { error } = await supabase
      .from('courses')
      .update({ status: 'rejected' })
      .eq('id', courseId);

    if (error) {
      console.error("Error rejecting course:", error);
      throw error;
    }

    // Create notification for the mentor
    try {
      const { error: notificationError } = await supabase
        .from('notifications')
        .insert({
          receiver_id: courseData.mentor_id,
          type: 'course_rejected',
          content: `Your course "${courseData.title}" has been rejected. Please review and make necessary changes before resubmitting.`,
          link: `/my-academy?courseId=${courseId}`,
          read: false
        });

      if (notificationError) {
        console.error("Error creating notification:", notificationError);
        // Don't throw here as the course rejection was successful
      } else {
        console.log("✅ Notification created successfully for mentor:", courseData.mentor_id);
      }
    } catch (notificationErr) {
      console.error("Exception creating notification:", notificationErr);
      // Don't throw here as the course rejection was successful
    }

    return loadPendingCourses();
  } catch (error) {
    console.error("Error in rejectCourse:", error);
    throw error;
  }
}

export async function removeRegisteredUser(email: string): Promise<boolean> {
  if (!email) {
    console.error("Cannot remove user: email is undefined");
    return false;
  }
  
  try {
    const { data: userData, error: userError } = await supabase
      .from('profiles')
      .select('id')
      .eq('email', email)
      .single();
    
    if (userError || !userData) {
      console.error("Error finding user by email:", userError);
      return false;
    }
    
    const { error: updateError } = await supabase
      .from('profiles')
      .update({ status: 'Inactive' })
      .eq('email', email);
    
    if (updateError) {
      console.error("Error updating user status in Supabase:", updateError);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error("Error attempting to remove user from Supabase:", error);
    return false;
  }
}

export async function deleteUserPermanently(email: string): Promise<boolean> {
  if (!email) {
    console.error("Cannot delete user: email is undefined");
    return false;
  }
  
  try {
    // Call the Edge Function to delete user permanently
    const { data, error } = await supabase.functions.invoke('delete-user-permanently', {
      body: { email }
    });
    
    if (error) {
      console.error("Error calling delete-user-permanently function:", error);
      return false;
    }
    
    if (data?.error) {
      console.error("Error from delete-user-permanently function:", data.error);
      return false;
    }
    
    console.log("User deleted successfully:", data?.message);
    return true;
  } catch (error) {
    console.error("Error attempting to delete user permanently:", error);
    return false;
  }
}

export async function verifyUserCredentials(email: string, password: string): Promise<{ success: boolean; userData: any; error: string | null }> {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    
    if (error) {
      return {
        success: false,
        userData: null,
        error: "Invalid email or password."
      };
    }
    
    if (data && data.user) {
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', data.user.id)
        .single();
      
      if (profileError) {
        console.error("Error fetching user profile:", profileError);
        return {
          success: false,
          userData: null,
          error: "Error fetching user profile."
        };
      }
      
      // Removed hardcoded admin checks - use database is_admin field only
      
      return {
        success: true,
        userData: {
          id: profileData.id,
          email: profileData.email,
          name: profileData.first_name && profileData.last_name ? 
            `${profileData.first_name} ${profileData.last_name}` : profileData.email,
          firstName: profileData.first_name,
          lastName: profileData.last_name,
          profileType: profileData.profile_type,
          isAdmin: profileData.is_admin,
          status: profileData.status,
          type: profileData.profile_type === 'mentor' ? 'Mentor' : 
                profileData.profile_type === 'mentee' ? 'Mentee' : 'User'
        },
        error: null
      };
    }
    
    return {
      success: false,
      userData: null,
      error: "Invalid email or password."
    };
  } catch (error) {
    console.error("Error in verifyUserCredentials:", error);
    return {
      success: false,
      userData: null,
      error: "An unexpected error occurred. Please try again."
    };
  }
}

export async function isAccountLocked(email: string): Promise<boolean> {
  if (!email) {
    console.error("Cannot check if account is locked: email is undefined");
    return false;
  }
  
  try {
    const { data, error } = await supabase
      .from('account_locks')
      .select('locked')
      .eq('email', email)
      .single();
    
    if (error) {
      console.error("Error checking if account is locked:", error);
      return false;
    }
    
    return data?.locked || false;
  } catch (error) {
    console.error("Error in isAccountLocked:", error);
    return false;
  }
}

export async function unlockUserAccount(email: string): Promise<boolean> {
  if (!email) {
    console.error("Cannot unlock account: email is undefined");
    return false;
  }
  
  try {
    const { data: existingData, error: checkError } = await supabase
      .from('account_locks')
      .select('*')
      .eq('email', email);
    
    if (checkError) {
      console.error("Error checking account lock:", checkError);
      return false;
    }
    
    if (existingData && existingData.length > 0) {
      const { error: updateError } = await supabase
        .from('account_locks')
        .update({ locked: false, attempts: 0 })
        .eq('email', email);
      
      if (updateError) {
        console.error("Error unlocking account in Supabase:", updateError);
        return false;
      }
    } else {
      const { error: insertError } = await supabase
        .from('account_locks')
        .insert([{ email, locked: false, attempts: 0 }]);
      
      if (insertError) {
        console.error("Error creating account lock record:", insertError);
        return false;
      }
    }
    
    return true;
  } catch (error) {
    console.error("Error attempting to unlock account in Supabase:", error);
    return false;
  }
}

export async function approveAccount(accountId: string): Promise<boolean> {
  if (!accountId) {
    console.error("Cannot approve account: accountId is undefined");
    return false;
  }
  
  try {
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', accountId)
      .single();
    
    if (profileError) {
      console.error("Error getting profile data:", profileError);
      return false;
    }
    
    if (profileData.profile_type === 'mentor') {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          profession: profileData.profession || 'Software Development',
          location: profileData.location || 'Remote',
          bio: profileData.bio || `Professional mentor with expertise in ${profileData.profession || 'technology'}.`,
          specialties: profileData.specialties || JSON.stringify(['Mentoring', 'Coaching']),
          status: 'Active',
          is_approved: true
        })
        .eq('id', accountId);
      
      if (updateError) {
        console.error("Error updating mentor profile:", updateError);
        return false;
      }
    } else {
      const { error } = await supabase
        .from('profiles')
        .update({ 
          status: 'Active',
          is_approved: true
        })
        .eq('id', accountId);
      
      if (error) {
        console.error("Error updating profile status in Supabase:", error);
        return false;
      }
    }
    
    // Send approval email notification
    try {
      console.log('Attempting to send approval email for:', profileData.email);
      
      const { data: emailData, error: emailError } = await supabase.functions.invoke('send-account-notification', {
        body: {
          email: profileData.email,
          firstName: profileData.first_name,
          lastName: profileData.last_name,
          notificationType: 'approval'
        }
      });

      if (emailError) {
        console.error('Failed to send approval email:', emailError);
        console.error('Email error details:', JSON.stringify(emailError, null, 2));
        // Don't fail the approval process if email fails
      } else {
        console.log('Approval email sent successfully:', emailData);
      }
    } catch (emailError) {
      console.error('Exception when sending approval email:', emailError);
      // Don't fail the approval process if email fails
    }
    
    console.log(`Profile with ID ${accountId} successfully approved`);
    return true;
  } catch (error) {
    console.error("Error approving account:", error);
    return false;
  }
}

export async function rejectAccount(accountId: string): Promise<boolean> {
  if (!accountId) {
    console.error("Cannot reject account: accountId is undefined");
    return false;
  }
  
  try {
    // Get user data before deletion for email notification
    const { data: userProfile, error: fetchError } = await supabase
      .from('profiles')
      .select('email, first_name, last_name')
      .eq('id', accountId)
      .single();

    if (fetchError) {
      console.error('Error fetching user profile for rejection:', fetchError);
      return false;
    }

    // Send rejection email notification before deletion
    try {
      console.log('Attempting to send rejection email for:', userProfile.email);
      
      const { data: emailData, error: emailError } = await supabase.functions.invoke('send-account-notification', {
        body: {
          email: userProfile.email,
          firstName: userProfile.first_name,
          lastName: userProfile.last_name,
          notificationType: 'rejection'
        }
      });

      if (emailError) {
        console.error('Failed to send rejection email:', emailError);
        console.error('Email error details:', JSON.stringify(emailError, null, 2));
        // Continue with deletion even if email fails
      } else {
        console.log('Rejection email sent successfully:', emailData);
      }
    } catch (emailError) {
      console.error('Exception when sending rejection email:', emailError);
      // Continue with deletion even if email fails
    }
    
    // Use the delete-user-permanently edge function for complete account removal
    try {
      console.log('Attempting to permanently delete user:', userProfile.email);
      
      const { data: deleteData, error: deleteError } = await supabase.functions.invoke('delete-user-permanently', {
        body: {
          email: userProfile.email
        }
      });

      if (deleteError) {
        console.error('Failed to permanently delete user:', deleteError);
        console.error('Delete error details:', JSON.stringify(deleteError, null, 2));
        return false;
      }

      console.log('User permanently deleted successfully:', deleteData);
      console.log(`User with email ${userProfile.email} successfully rejected and permanently deleted`);
      return true;
    } catch (deleteError) {
      console.error('Exception when permanently deleting user:', deleteError);
      return false;
    }
  } catch (error) {
    console.error("Error rejecting account:", error);
    return false;
  }
}

export async function updateUserStatus(userId: string, newStatus: 'Active' | 'Inactive' | 'Pending' | 'Locked'): Promise<User[]> {
  try {
    const { error } = await supabase
      .from('profiles')
      .update({ status: newStatus })
      .eq('id', userId);
    
    if (error) {
      console.error("Error updating user status in Supabase:", error);
      return [];
    }
    
    return await loadAllUsers();
  } catch (error) {
    console.error("Error in updateUserStatus:", error);
    return [];
  }
}
